import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';
import polarisLogo from '../images/Polaris Logo.svg';
import '../styles/clever-confirm-page.css';
import { apiClient } from "../utils/apiClient";

interface CleverInfo {
  cleverId: string;
  cleverDistrictId: string;
  cleverDistrictName: string | null;
  cleverSchoolId: string | null;
  cleverSchoolName: string | null;
}

interface LocationState {
  priceId: string;
  billingInterval: 'month' | 'year';
  planName?: string;
  customerEmail: string;
  clever: CleverInfo;
}

interface ReadOnlyFieldProps {
  label: string;
  value: string | null | undefined;
  placeholder?: string;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value, placeholder = '—' }) => (
  <div className="ccf-field">
    <label className="ccf-label">{label}</label>
    <div className={`ccf-value ${!value ? 'ccf-value--empty' : ''}`}>
      {value || placeholder}
    </div>
  </div>
);

type SchoolCheckState = 'idle' | 'checking' | 'available' | 'taken' | 'error';

const DEBOUNCE_MS = 450;

async function postCleverSchoolAvailability(
  cleverDistrictId: string,
  schoolName: string,
  signal: AbortSignal
): Promise<{ ok: boolean; available?: boolean }> {
  const res = await apiClient(buildApiUrl('api/subscriptions/clever-school-availability'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      cleverDistrictId,
      schoolName,
    }),
    signal,
  });
  const data = (await res.json().catch(() => ({}))) as { available?: boolean };
  if (!res.ok) {
    return { ok: false };
  }
  return { ok: true, available: data.available === true };
}

export const CleverConfirmPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [enteredSchoolName, setEnteredSchoolName] = useState('');
  const [schoolCheckState, setSchoolCheckState] = useState<SchoolCheckState>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestIdRef = useRef(0);

  const state = location.state as LocationState | undefined;
  const { priceId, billingInterval, planName, customerEmail, clever } = state || {};

  useEffect(() => {
    if (!priceId || !clever?.cleverDistrictId) {
      navigate('/signup', { replace: true });
    }
  }, [priceId, clever, navigate]);

  useEffect(() => {
    const trimmed = enteredSchoolName.trim();
    if (!trimmed || !clever?.cleverDistrictId) {
      setSchoolCheckState('idle');
      return;
    }

    const id = ++requestIdRef.current;
    setSchoolCheckState('checking');

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const result = await postCleverSchoolAvailability(clever.cleverDistrictId, trimmed, controller.signal);
        if (id !== requestIdRef.current) {
          return;
        }
        if (!result.ok) {
          setSchoolCheckState('error');
          return;
        }
        setSchoolCheckState(result.available ? 'available' : 'taken');
      } catch (e: unknown) {
        if (id !== requestIdRef.current) {
          return;
        }
        if (e instanceof DOMException && e.name === 'AbortError') {
          return;
        }
        setSchoolCheckState('error');
      }
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [enteredSchoolName, clever?.cleverDistrictId]);

  if (!priceId || !clever?.cleverDistrictId) {
    return null;
  }

  const trimmedSchool = enteredSchoolName.trim();
  const checkoutEnabled =
    trimmedSchool.length > 0 && schoolCheckState === 'available' && !isSubmitting;

  const handleConfirm = async () => {
    setSubmitError(null);
    if (!trimmedSchool) {
      setSubmitError('Enter your school name to continue.');
      return;
    }
    if (schoolCheckState !== 'available') {
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await postCleverSchoolAvailability(clever.cleverDistrictId, trimmedSchool, new AbortController().signal);
      if (!result.ok) {
        setSubmitError('Unable to verify school. Try again.');
        return;
      }
      if (!result.available) {
        setSchoolCheckState('taken');
        setSubmitError(null);
        return;
      }
      navigate('/checkout', {
        state: {
          priceId,
          billingInterval,
          customerEmail,
          cleverId: clever.cleverId,
          cleverDistrictId: clever.cleverDistrictId,
          cleverDistrictName: clever.cleverDistrictName,
          cleverSchoolId: clever.cleverSchoolId,
          cleverSchoolName: clever.cleverSchoolName,
          enteredSchoolName: trimmedSchool,
        },
      });
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSchoolChange = (value: string) => {
    setEnteredSchoolName(value);
    setSubmitError(null);
  };

  const handleBack = () => {
    navigate('/subscribe/details', {
      state: { priceId, billingInterval, planName },
    });
  };

  return (
    <div className="clever-confirm-page">
      <div className="clever-confirm-container">

        <div className="ccf-logo">
          <img src={polarisLogo} alt="Polaris Athlete" />
        </div>

        <div className="ccf-clever-badge">
          <span className="ccf-clever-dot" />
          Clever account verified
        </div>

        <h1 className="ccf-title">Confirm your school details</h1>
        {planName && <p className="ccf-plan">Plan: <strong>{planName}</strong></p>}
        <p className="ccf-subtitle">
          Enter the name of your school for this subscription. We link it to your Clever district and
          prevent duplicate subscriptions for the same district and school.
        </p>

        <div className="ccf-section">
          <h2 className="ccf-section-title">Your Clever account</h2>
          <ReadOnlyField label="Email address" value={customerEmail} placeholder="Not provided" />
        </div>

        <div className="ccf-section">
          <h2 className="ccf-section-title">District</h2>
          <ReadOnlyField label="District name" value={clever.cleverDistrictName} placeholder="Not available" />
        </div>

        <div className="ccf-section">
          <h2 className="ccf-section-title">School</h2>
          <div className="ccf-field">
            <label className="ccf-label" htmlFor="entered-school-name">School name for this subscription *</label>
            <input
              id="entered-school-name"
              className="ccf-input"
              type="text"
              autoComplete="organization"
              value={enteredSchoolName}
              onChange={(e) => handleSchoolChange(e.target.value)}
              placeholder="e.g. Lincoln High School"
            />
          </div>
          {schoolCheckState === 'checking' && (
            <p className="ccf-status ccf-status--checking">Checking availability…</p>
          )}
          {schoolCheckState === 'available' && (
            <p className="ccf-status ccf-status--available">
              This school name is available for your district.
            </p>
          )}
          {schoolCheckState === 'taken' && (
            <p className="ccf-error">
              A subscription already exists for this district and school name.
            </p>
          )}
          {schoolCheckState === 'error' && (
            <p className="ccf-error">Unable to verify school. Try again.</p>
          )}
          {submitError && <p className="ccf-error">{submitError}</p>}
        </div>

        <div className="ccf-actions">
          <button
            type="button"
            className="ccf-confirm-btn"
            onClick={handleConfirm}
            disabled={!checkoutEnabled}
          >
            {isSubmitting ? 'Checking…' : 'Checkout'}
          </button>
          <button type="button" className="ccf-back-btn" onClick={handleBack} disabled={isSubmitting}>
            Back — change details
          </button>
        </div>

      </div>
    </div>
  );
};

export default CleverConfirmPage;
